import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { InstrumentFixDraft } from "../components/InstrumentEditor";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";
import { DEFAULT_FILTERS } from "./constants";
import {
  CatalogFilters,
  EditSection,
  InstrumentCatalogProviderProps,
  InstrumentCatalogSession,
  InstrumentDraftUpdater,
} from "./types";

const InstrumentCatalogContext = createContext<InstrumentCatalogSession | null>(
  null,
);

const withUpdatedDraft = (
  drafts: Record<number, InstrumentFixDraft>,
  instrumentId: number,
  updater: InstrumentDraftUpdater,
) => {
  const next = updater(drafts[instrumentId] ?? null);
  if (next !== null) {
    return { ...drafts, [instrumentId]: next };
  }
  if (!(instrumentId in drafts)) {
    return drafts;
  }
  const { [instrumentId]: _dropped, ...remaining } = drafts;
  return remaining;
};

// Lives above the routes so a page change keeps it, and inside the shell so logging out drops it.
export const InstrumentCatalogProvider: React.FC<
  InstrumentCatalogProviderProps
> = ({ children }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<InstrumentCatalogSession["drafts"]>({});
  const [errors, setErrors] = useState<InstrumentCatalogSession["errors"]>({});

  useUnsavedChangesGuard(Object.keys(drafts).length > 0);

  const changeFilters = useCallback(
    (changes: Partial<CatalogFilters>) =>
      setFilters((current) => ({ ...current, ...changes })),
    [],
  );

  const updateDraft = useCallback(
    (instrumentId: number, updater: InstrumentDraftUpdater) =>
      setDrafts((current) => withUpdatedDraft(current, instrumentId, updater)),
    [],
  );

  const setSectionError = useCallback(
    (instrumentId: number, section: EditSection, message: string | null) =>
      setErrors((current) => ({
        ...current,
        [instrumentId]: {
          ...current[instrumentId],
          [section]: message ?? undefined,
        },
      })),
    [],
  );

  const clearErrors = useCallback(
    (instrumentId: number) =>
      setErrors(({ [instrumentId]: _cleared, ...others }) => others),
    [],
  );

  const session = useMemo(
    () => ({
      filters,
      selectedId,
      drafts,
      errors,
      changeFilters,
      select: setSelectedId,
      updateDraft,
      setSectionError,
      clearErrors,
    }),
    [
      filters,
      selectedId,
      drafts,
      errors,
      changeFilters,
      updateDraft,
      setSectionError,
      clearErrors,
    ],
  );

  return (
    <InstrumentCatalogContext.Provider value={session}>
      {children}
    </InstrumentCatalogContext.Provider>
  );
};

export const useInstrumentCatalogSession = (): InstrumentCatalogSession => {
  const session = useContext(InstrumentCatalogContext);
  if (!session) {
    throw new Error(
      "useInstrumentCatalogSession must be used inside InstrumentCatalogProvider",
    );
  }
  return session;
};
